import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFgp } from "@/context/FgpContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { User, Mail, Lock, Building2, ArrowRight, ShieldCheck, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const AUTH_BG = "https://images.unsplash.com/photo-1587301552314-4dabb468fdd5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Register() {
  const { register } = useFgp();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", workspaceName: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.password.trim()) return toast.error("Please complete all fields");
    if (f.password.length < 6) return toast.error("Password must be at least 6 characters");
    setLoading(true);
    try {
      await register(f);
      toast.success("Workspace created — welcome to Swishr Central");
      navigate("/app/dashboard");
    } catch (err) {
      toast.error(err.message || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex">
      {/* Left visual */}
      <div className="hidden lg:flex w-1/2 relative bg-slate-900">
        <img src={AUTH_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" data-testid="register-logo-home-link"><Logo light /></Link>
          <div>
            <h2 className="font-heading text-3xl font-semibold leading-tight max-w-md">Your private command centre starts here.</h2>
            <p className="mt-4 text-slate-300 max-w-md">Create your workspace and manage every principal's people, documents, renewals, calendar and tasks — securely, in one place.</p>
            <ul className="mt-6 space-y-2 text-sm text-slate-300">
              {["Your own isolated workspace", "Free trial — no card required", "Set up in under a minute"].map((t) => (
                <li key={t} className="flex items-center gap-2.5"><span className="h-5 w-5 rounded-full bg-white/10 flex items-center justify-center"><ShieldCheck className="h-3 w-3" /></span>{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><Link to="/" data-testid="register-logo-home-link-mobile"><Logo /></Link></div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">Create your account</h1>
          <p className="mt-1 text-sm text-slate-500">Start your free Swishr Central workspace.</p>

          <button onClick={googleLogin} data-testid="google-register-btn" type="button"
            className="mt-8 w-full flex items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4 w-4" />
            Sign up with Google
          </button>
          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="register-form">
            <div className="space-y-1.5">
              <Label className="text-slate-700">Full name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={f.name} onChange={set("name")} placeholder="Amelia Hart" className="pl-9" data-testid="register-name" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700">Work email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input type="email" value={f.email} onChange={set("email")} placeholder="you@company.com" className="pl-9" data-testid="register-email" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700">Workspace / company name</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input value={f.workspaceName} onChange={set("workspaceName")} placeholder="Vance Private Office" className="pl-9" data-testid="register-workspace" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input type="password" value={f.password} onChange={set("password")} placeholder="••••••••" className="pl-9" data-testid="register-password" />
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#516670] hover:bg-[#41545d] gap-2" data-testid="register-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create workspace <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>

          <p className="mt-6 text-sm text-slate-500 text-center">
            Already have an account? <Link to="/login" className="text-neutral-700 font-medium hover:underline" data-testid="to-login-link">Log in</Link>
          </p>
          <p className="mt-6 text-center text-xs text-slate-400">
            <Link to="/" className="hover:text-slate-600">← Back to home</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
